/**
 * HomePage — `/` route.
 *
 * Minimal greeting + 4 quick-action tiles. Replaces the inline
 * profile/wallet/loyalty/BU section the monolith rendered at `/`.
 * The dedicated launcher grid lives at `/launcher` (ticket
 * superall-shell-06).
 */

import * as React from 'react';
import { Link } from 'wouter';
import { StaggerList, StaggerItem, MotionCard, AnimatedNumber, FadeIn } from '@workspace/ui';

import { useAuth } from '../providers/AuthProvider';
import { useT, useLang } from '../providers/LangProvider';
import { apiGetJSON } from '../lib/api';

interface Tile {
  href: string;
  emoji: string;
  title: string;
  sub: string;
}

// ─── Cross-BU Activity types ──────────────────────────────────────────────────

interface ActivityItem {
  id: string;
  type: 'order' | 'booking' | 'points' | 'review';
  bu: string;
  actionEn: string;
  actionAr: string;
  pointsChange: number | null;
  occurredAt: string;
}

const TYPE_ICONS: Record<string, string> = {
  order: '🛍️',
  booking: '🎬',
  points: '⭐',
  review: '✍️',
};

const BU_ICONS: Record<string, string> = {
  'O Mall': '🏬',
  Cinema: '🎬',
  FnB: '🍔',
  Residence: '🏠',
  default: '⭐',
};

function timeAgo(iso: string, isAr: boolean): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);
  if (isAr) {
    if (mins < 2) return 'الآن';
    if (mins < 60) return `منذ ${mins} دقيقة`;
    if (hours < 24) return `منذ ${hours} ساعة`;
    return `منذ ${days} يوم`;
  }
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

// ─── Loyalty snapshot ─────────────────────────────────────────────────────────

interface LoyaltySnapshot {
  availablePoints: number;
  tier: string;
  tierProgressPct: number;
  nextTier: string | null;
}

const TIER_LABELS_EN: Record<string, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
  black: 'Black',
};

const TIER_LABELS_AR: Record<string, string> = {
  bronze: 'برونزي',
  silver: 'فضي',
  gold: 'ذهبي',
  platinum: 'بلاتيني',
  black: 'أسود',
};

export function HomePage(): React.ReactElement {
  const { user } = useAuth();
  const t = useT();
  const [lang] = useLang();
  const isAr = lang === 'ar';

  const tiles: Tile[] = [
    {
      href: '/wallet',
      emoji: '💳',
      title: t.home.walletTile,
      sub: t.home.walletTileSub,
    },
    {
      href: '/loyalty',
      emoji: '⭐',
      title: t.home.loyaltyTile,
      sub: t.home.loyaltyTileSub,
    },
    {
      href: '/notifications',
      emoji: '🔔',
      title: t.home.notificationsTile,
      sub: t.home.notificationsTileSub,
    },
    {
      href: '/launcher',
      emoji: '🧭',
      title: t.home.launcherTile,
      sub: t.home.launcherTileSub,
    },
  ];

  // Cross-BU quick actions
  const buActions = [
    { href: '/mall', emoji: '🛍️', labelEn: 'Shop', labelAr: 'تسوّق' },
    { href: '/cinema', emoji: '🎬', labelEn: 'Cinema', labelAr: 'سينما' },
    { href: '/fnb', emoji: '🍔', labelEn: 'FnB', labelAr: 'مطاعم' },
    { href: '/residence', emoji: '🏠', labelEn: 'Residence', labelAr: 'السكن' },
  ];

  // Activity feed
  const [activityItems, setActivityItems] = React.useState<ActivityItem[] | null>(null);
  const [activityLoading, setActivityLoading] = React.useState(true);
  const [activityError, setActivityError] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setActivityLoading(true);
    setActivityError(false);
    apiGetJSON<{ items: ActivityItem[] }>('/api/v1/superall/activity')
      .then((data) => {
        if (!cancelled) {
          setActivityItems(data.items);
          setActivityLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setActivityError(true);
          setActivityLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Loyalty snapshot
  const [loyaltyData, setLoyaltyData] = React.useState<LoyaltySnapshot | null>(null);
  const [loyaltyLoading, setLoyaltyLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoyaltyLoading(true);
    apiGetJSON<LoyaltySnapshot>('/api/v1/superall/loyalty-snapshot')
      .then((data) => {
        if (!cancelled) {
          setLoyaltyData(data);
          setLoyaltyLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoyaltyLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <FadeIn>
      {/* Greeting */}
      <section className="mb-6 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-5 shadow-sm">
        <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          {t.home.greeting}
        </p>
        <h2 className="mt-1 text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
          {user?.fullName ?? t.title}
        </h2>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{t.tagline}</p>
      </section>

      {/* Cross-BU Quick Actions row */}
      <section aria-label={isAr ? 'وصول سريع للخدمات' : 'BU Quick Actions'} className="mb-6">
        <h3 className="mb-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
          {isAr ? 'الخدمات' : 'Services'}
        </h3>
        <StaggerList className="grid grid-cols-4 gap-2">
          {buActions.map((action) => (
            <StaggerItem key={action.href}>
              <Link
                href={action.href}
                className="flex flex-col items-center gap-1.5 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 py-3 text-center transition hover:border-purple-300 hover:shadow-sm"
              >
                <span className="text-2xl" aria-hidden="true">
                  {action.emoji}
                </span>
                <span className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-300">
                  {isAr ? action.labelAr : action.labelEn}
                </span>
              </Link>
            </StaggerItem>
          ))}
        </StaggerList>
      </section>

      {/* Original tiles */}
      <section aria-label={t.home.quickActions} className="mb-6">
        <h3 className="mb-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
          {t.home.quickActions}
        </h3>
        <StaggerList className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {tiles.map((tile) => (
            <StaggerItem key={tile.href}>
              <Link
                href={tile.href}
                className="flex flex-col rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 text-start transition hover:border-purple-400 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
              >
                <div className="mb-2 text-2xl" aria-hidden="true">
                  {tile.emoji}
                </div>
                <div className="font-semibold leading-tight text-neutral-900 dark:text-neutral-100">
                  {tile.title}
                </div>
                <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 leading-snug">
                  {tile.sub}
                </div>
              </Link>
            </StaggerItem>
          ))}
        </StaggerList>
      </section>

      {/* Cross-BU Activity Feed */}
      <section aria-label={isAr ? 'النشاط الأخير' : 'Recent Activity'} className="mb-6">
        <h3 className="mb-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
          {isAr ? 'النشاط الأخير' : 'Recent Activity'}
        </h3>
        {activityLoading && (
          <div className="rounded-xl border border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 p-4 text-center text-xs text-neutral-400">
            {isAr ? 'جاري التحميل…' : 'Loading…'}
          </div>
        )}
        {activityItems && activityItems.length > 0 && (
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-none">
            <StaggerList className="flex gap-3">
              {activityItems.map((item) => (
                <StaggerItem key={item.id}>
                  <MotionCard hoverScale={1.02} tapScale={0.98} className="w-52 shrink-0">
                    <div className="flex flex-col rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3 shadow-sm h-full">
                      <div className="flex items-center gap-2">
                        <span className="text-lg" aria-hidden="true">
                          {TYPE_ICONS[item.type] ?? '⭐'}
                        </span>
                        <span className="text-xs font-semibold text-neutral-600 dark:text-neutral-300">
                          {BU_ICONS[item.bu] ?? '⭐'} {item.bu}
                        </span>
                      </div>
                      <p className="mt-2 text-xs leading-snug text-neutral-800 dark:text-neutral-200 font-medium">
                        {isAr ? item.actionAr : item.actionEn}
                      </p>
                      {item.pointsChange !== null && (
                        <p
                          className={`mt-1.5 text-xs font-semibold tabular-nums ${
                            item.pointsChange >= 0 ? 'text-emerald-600' : 'text-rose-600'
                          }`}
                        >
                          {item.pointsChange >= 0 ? '+' : ''}
                          {item.pointsChange} {isAr ? 'نقطة' : 'pts'}
                        </p>
                      )}
                      <p className="mt-auto pt-2 text-[10px] text-neutral-400 dark:text-neutral-500">
                        {timeAgo(item.occurredAt, isAr)}
                      </p>
                    </div>
                  </MotionCard>
                </StaggerItem>
              ))}
            </StaggerList>
          </div>
        )}
        {activityError && (
          <p className="text-xs text-neutral-400">
            {isAr ? 'تعذّر تحميل النشاط' : 'Could not load activity'}
          </p>
        )}
        {!activityLoading && !activityError && activityItems?.length === 0 && (
          <div className="rounded-xl border border-dashed border-neutral-200 dark:border-neutral-700 p-6 text-center text-xs text-neutral-400">
            {isAr ? 'لا يوجد نشاط بعد' : 'No activity yet'}
          </div>
        )}
      </section>

      {/* Loyalty Snapshot */}
      <section aria-label={isAr ? 'ملخص النقاط' : 'Loyalty Snapshot'} className="mb-6">
        {loyaltyLoading && (
          <div className="rounded-xl border border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 p-4 text-center text-xs text-neutral-400">
            {isAr ? 'جاري التحميل…' : 'Loading…'}
          </div>
        )}
        {loyaltyData && (
          <MotionCard hoverScale={1.01} tapScale={0.99} className="rounded-2xl">
            <div className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    {isAr ? 'نقاطك' : 'Your Points'}
                  </p>
                  <p className="mt-1 text-4xl font-bold tabular-nums text-neutral-900 dark:text-neutral-100">
                    <AnimatedNumber value={loyaltyData.availablePoints} />
                  </p>
                </div>
                <span className="rounded-full bg-amber-100 dark:bg-amber-900/40 px-3 py-1 text-xs font-bold text-amber-700 dark:text-amber-400">
                  {isAr
                    ? (TIER_LABELS_AR[loyaltyData.tier] ?? loyaltyData.tier)
                    : (TIER_LABELS_EN[loyaltyData.tier] ?? loyaltyData.tier)}
                </span>
              </div>
              {loyaltyData.nextTier && (
                <div className="mt-4">
                  <div className="mb-1 flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
                    <span>
                      {isAr ? 'التقدم نحو' : 'Progress to'}{' '}
                      {isAr
                        ? (TIER_LABELS_AR[loyaltyData.nextTier] ?? loyaltyData.nextTier)
                        : (TIER_LABELS_EN[loyaltyData.nextTier] ?? loyaltyData.nextTier)}
                    </span>
                    <span className="font-medium">{loyaltyData.tierProgressPct}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
                      style={{ width: `${loyaltyData.tierProgressPct}%` }}
                    />
                  </div>
                </div>
              )}
              <Link
                href="/loyalty"
                className="mt-4 flex items-center justify-center rounded-xl border border-neutral-200 dark:border-neutral-700 py-2 text-xs font-semibold text-neutral-700 dark:text-neutral-300 transition hover:border-purple-300"
              >
                {isAr ? 'عرض التفاصيل ←' : 'View Details →'}
              </Link>
            </div>
          </MotionCard>
        )}
      </section>
    </FadeIn>
  );
}
